import { useState, useRef, useEffect, useCallback, useMemo } from "react";

const DEFAULT_POST_COUNT = 15;

type Cursor = { timestamp: string; id: string };

// Ensure that each item has an `id` and `createdAt` field
interface CursorItem {
  id: string;
  createdAt?: string | null;
}

interface PaginationOptions<T extends CursorItem> {
  fetchMore: (variables: any) => Promise<any>;
  initialData?: T[];
  pageSize?: number;
  getLastCursor?: (items: T[]) => Cursor | null;
  extractNewItems?: (result: any) => T[];
  onError?: (error: any) => void;
  additionalVariables?: Record<string, any>;
  skip?: boolean;
  getItemId?: (item: T) => string;
}

interface PaginationState<T> {
  items: T[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

export function usePagination<T extends CursorItem>({
  fetchMore,
  initialData = [],
  pageSize = DEFAULT_POST_COUNT,
  getLastCursor,
  extractNewItems,
  onError,
  additionalVariables = {},
  skip = false,
  getItemId,
}: PaginationOptions<T>): PaginationState<T> {
  const [items, setItems] = useState<T[]>(initialData);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (isFirstLoad.current && initialData.length > 0) {
      setItems(initialData);
      setHasMore(initialData.length >= pageSize);
      isFirstLoad.current = false;
    }
  }, [initialData, pageSize]);

  const getItemIdLocal = useCallback(
    getItemId ?? ((item: any) => item.comment_id || item.id),
    [getItemId]
  );

  const loadMore = useCallback(async () => {
    if (skip || !hasMore || loading || (!items.length && !isFirstLoad.current))
      return;

    setLoading(true);

    try {
      let cursor = null;
      if (getLastCursor && items.length > 0) {
        cursor = getLastCursor(items);
      }

      const result = await fetchMore({
        variables: {
          input: {
            ...additionalVariables,
            pagination: {
              first: pageSize,
              after_time: cursor?.timestamp || null,
              after_uid: cursor?.id || null,
            },
          },
        },
      });

      const newItems: T[] = extractNewItems
        ? extractNewItems(result)
        : result?.data?.items || [];

      const existingIds = new Set(items.map(getItemIdLocal));
      const uniqueNewItems = newItems.filter(
        (item) => !existingIds.has(getItemIdLocal(item))
      );

      if (uniqueNewItems.length > 0) {
        setItems((prev) => [...prev, ...uniqueNewItems]);
        setHasMore(newItems.length >= pageSize && uniqueNewItems.length > 0);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading more items:", error);
      setHasMore(false);
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }
  }, [
    skip,
    hasMore,
    loading,
    items,
    getLastCursor,
    fetchMore,
    additionalVariables,
    pageSize,
    extractNewItems,
    onError,
    getItemIdLocal,
  ]);

  // Memoize the return object to prevent unnecessary re-renders
  const memoizedReturn = useMemo(
    () => ({
      items,
      loading,
      hasMore,
      loadMore,
    }),
    [items, loading, hasMore, loadMore]
  );

  return memoizedReturn;
}
